import { useSearchParams, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { AdminPageHeader } from '@/features/admin/components/AdminPageHeader';
import { FinanceOverviewPanel } from '@/features/admin-finance/components/FinanceOverviewPanel';
import { FinanceWithdrawalsPanel } from '@/features/admin-finance/components/FinanceWithdrawalsPanel';
import { FinanceSettlementsPanel } from '@/features/admin-finance/components/FinanceSettlementsPanel';
import { FinanceDisputesPanel } from '@/features/admin-finance/components/FinanceDisputesPanel';
import { FinanceWalletsPanel } from '@/features/admin-finance/components/FinanceWalletsPanel';
import { FinanceCommercialPanel } from '@/features/admin-finance/components/FinanceCommercialPanel';
import { FinanceBankAccountsPanel } from '@/features/admin-finance/components/FinanceBankAccountsPanel';
import { financeShellSx, financeTabsSx } from '@/features/admin-finance/styles/financeSurfaces';

const financeTabs = [
  'overview',
  'withdrawals',
  'settlements',
  'wallets',
  'commercial',
  'bank-accounts',
  'disputes',
] as const;

type FinanceTabKey = (typeof financeTabs)[number];

function getFinanceTab(tab: string | null): FinanceTabKey {
  return financeTabs.includes(tab as FinanceTabKey) ? (tab as FinanceTabKey) : 'overview';
}

const tabLabels: Record<FinanceTabKey, string> = {
  overview: 'Overview',
  withdrawals: 'Withdrawals',
  settlements: 'Settlements',
  wallets: 'Wallets',
  commercial: 'Commercial',
  'bank-accounts': 'Bank accounts',
  disputes: 'Disputes',
};

export function AdminFinancePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getFinanceTab(searchParams.get('tab'));

  const setTab = (tab: FinanceTabKey) => {
    setSearchParams(tab === 'overview' ? {} : { tab }, { replace: true });
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <AdminPageHeader
        title="Finance"
        subtitle="Platform earnings, settlements, withdrawals, and disputes."
      />

      <Box sx={financeShellSx}>
        <Tabs
          value={activeTab}
          onChange={(_, value: FinanceTabKey) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={financeTabsSx}
        >
          {financeTabs.map((tab) => (
            <Tab key={tab} value={tab} label={tabLabels[tab]} disableRipple />
          ))}
        </Tabs>

        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {activeTab === 'overview' && (
            <FinanceOverviewPanel onNavigateTab={(tab) => setTab(getFinanceTab(tab))} />
          )}
          {activeTab === 'withdrawals' && <FinanceWithdrawalsPanel />}
          {activeTab === 'settlements' && <FinanceSettlementsPanel />}
          {activeTab === 'wallets' && <FinanceWalletsPanel />}
          {activeTab === 'commercial' && <FinanceCommercialPanel />}
          {activeTab === 'bank-accounts' && <FinanceBankAccountsPanel />}
          {activeTab === 'disputes' && <FinanceDisputesPanel />}
        </Box>
      </Box>
    </Box>
  );
}

export function AdminFinanceWithdrawalsRedirect() {
  return <Navigate to="/admin/finance?tab=withdrawals" replace />;
}

export function AdminFinanceSettlementsRedirect() {
  return <Navigate to="/admin/finance?tab=settlements" replace />;
}

export function AdminDisputesRedirect() {
  return <Navigate to="/admin/finance?tab=disputes" replace />;
}
